import React, { useState } from "react";
import { Alert, Button, Card, Modal, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import ThreePLPartnerDropdown from "../ThreePLPartnerDropdown";
import { isOrderCancelled } from "../../utilities/paymentStatus";

/* A rider can be chosen only while the order is still being processed. For
   every other state the panel used to render the rider select greyed out
   with a blocked cursor, which told the reader nothing about WHY. Each state
   now says what it is and what, if anything, comes next. */
const DONE_STATUSES = new Set(["DELIVERED", "FULFILLED"]);
const riderNameOf = (order) => {
  const r = order?.riderId;
  if (!r || typeof r !== "object") return null;
  return [r.firstName, r.lastName].filter(Boolean).join(" ").trim() || null;
};
const closedAssignmentNotice = (order) => {
  const status = String(order?.delivery_status || "").toUpperCase();
  if (isOrderCancelled(order)) {
    const c = order?.cancellation || {};
    const reason = c.reasonLabel || c.reason;
    const by = c.cancelledByName;
    const detail = [reason ? `Reason: ${reason}` : null, by ? `by ${by}` : null].filter(Boolean).join(", ");
    return {
      tone: "secondary",
      title: "Order cancelled",
      body: `No rider is needed.${detail ? ` ${detail}.` : ""}`,
    };
  }
  if (DONE_STATUSES.has(status)) {
    const rider = riderNameOf(order);
    return {
      tone: "success",
      title: "Delivered",
      body: rider ? `Delivered by ${rider}. Nothing left to assign.` : "This order is complete. Nothing left to assign.",
    };
  }
  return null;
};

const API_URL = process.env.REACT_APP_SERVER_URL;

// Both assignment paths use the server's existing order-actor and payment
// checks. The admin brand-order screen previously exposed only the rider path.
export default function OrderAssignmentPanel({ order, onChange, children }) {
  const [mode, setMode] = useState("rider");
  const [partnerId, setPartnerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRecall, setShowRecall] = useState(false);
  const partner = order?.assignmentDetails?.partnerId;
  const canAssign = order?.delivery_status === "PROCESSING" && !order?.riderId && !partner;
  const canRecall = !!partner && order?.delivery_status === "PENDING_3PL";

  const submit = async (recall = false) => {
    if (busy || (recall ? !canRecall : !canAssign || !partnerId)) return;
    setBusy(true);
    setShowRecall(false);
    try {
      const { data } = await axios.post(
        `${API_URL}/weborders/${order._id}/${recall ? "recall-from-3pl" : "assign-to-3pl"}`,
        recall ? { reason: "Recalled by platform admin" } : { partnerId },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (!data?.success) {
        toast.error(data?.error?.message || data?.message || "Could not update 3PL assignment.");
        return;
      }
      toast.success(data.message || (recall ? "Order recalled from 3PL partner." : "Order sent to 3PL partner."));
      setPartnerId("");
      setMode("rider");
      onChange();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || error.response?.data?.message || "Could not update 3PL assignment.");
    } finally {
      setBusy(false);
    }
  };

  const closed = closedAssignmentNotice(order);
  if (closed) {
    return (
      <Card className="p-3 shadow mt-3 mb-3" aria-label="Delivery assignment">
        <Alert variant={closed.tone} className="mb-0">
          <strong>{closed.title}.</strong> {closed.body}
        </Alert>
      </Card>
    );
  }

  return (
    <Card className="p-3 shadow mt-3 mb-3" aria-label="Delivery assignment">
      {canAssign && (
        <div className="mb-3">
          <div className="mb-2">Assignment Type</div>
          <div className="d-flex flex-wrap gap-2">
            <Button size="sm" variant={mode === "rider" ? "primary" : "outline-primary"}
              aria-pressed={mode === "rider"} disabled={busy} onClick={() => setMode("rider")}>
              Assign to Rider
            </Button>
            <Button size="sm" variant={mode === "3pl" ? "success" : "outline-success"}
              aria-pressed={mode === "3pl"} disabled={busy} onClick={() => setMode("3pl")}>
              Assign to 3PL Partner
            </Button>
          </div>
        </div>
      )}
      {partner && (
        <Alert variant="info" className="mb-2">
          Sent to: <strong>{partner.companyName || "3PL partner"}</strong>
          {order.delivery_status === "PENDING_3PL" && <div>Waiting for the 3PL partner to assign a rider.</div>}
          {canRecall && (
            <Button variant="outline-danger" size="sm" className="mt-2" disabled={busy}
              onClick={() => setShowRecall(true)}>Recall Order</Button>
          )}
        </Alert>
      )}
      {canAssign && mode === "3pl" ? (
        <>
          <ThreePLPartnerDropdown dropoff={order?.shippingAddress?.latLng}
            selectedPartnerId={partnerId} onSelectPartner={setPartnerId} disabled={busy} />
          {partnerId && (
            <Button variant="success" className="mt-2" disabled={busy} onClick={() => submit()}>
              {busy ? <><Spinner animation="border" size="sm" className="me-2" />Sending...</> : "Send to 3PL Partner"}
            </Button>
          )}
        </>
      ) : order?.delivery_status !== "PENDING_3PL" && children}
      <Modal show={showRecall} onHide={() => setShowRecall(false)}>
        <Modal.Header closeButton><Modal.Title>Recall Order from 3PL</Modal.Title></Modal.Header>
        <Modal.Body>
          Recall this order from {partner?.companyName || "the 3PL partner"}?
          It will return to Processing so you can assign it again.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRecall(false)}>Cancel</Button>
          <Button variant="danger" disabled={busy} onClick={() => submit(true)}>Confirm Recall</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
